import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useDocs } from "../app/docs-context";
import { useReadTracking } from "../features/read-tracking/use-read-tracking";
import { RestrictedError } from "../shared/api/client";
import type { TocEntry } from "../shared/api/types";
import { sanitizeArticleHtml } from "../shared/lib/safe-html";
import { useAsync } from "../shared/lib/use-async";
import { ErrorState, LoadingState } from "../shared/ui/states";
import { SignInGate } from "./signin-gate";

/** Distance from the top at which a heading counts as the one being read. */
const SCROLL_SPY_OFFSET = 90;

export function ArticlePage() {
  const { slug = "" } = useParams();
  const { api, info, paths } = useDocs();
  const { data, error, loading, reload } = useAsync(
    (signal) => api.article(slug, signal),
    [api, slug],
  );

  useReadTracking(api, data?.slug);

  useEffect(() => {
    // A new article starts at the top; without this, following a link from
    // halfway down one page lands halfway down the next.
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (data?.title) document.title = `${data.title} · ${info.name} documentation`;
  }, [data?.title, info.name]);

  if (loading) return <LoadingState />;

  // The API answers a gated article with the role required and its title, which
  // is exactly what the sign-in page needs to name what is behind it.
  if (error instanceof RestrictedError) {
    return <SignInGate title={error.title} requiredRole={error.requiredRole} onSignedIn={reload} />;
  }

  if (error || !data)
    return (
      <ErrorState
        title="Article not found"
        description="This document may have been moved or unpublished."
        onRetry={reload}
      />
    );

  // The host sends both; the reader shows the later one, and only when the product's settings say so.
  const lastUpdated = data.updatedAt ?? data.publishedAt;

  return (
    <div className="flex items-start gap-0">
      <main className="mx-auto min-w-0 max-w-[760px] flex-1 px-12 pb-16 pt-10">
        <nav
          className="mb-5 flex flex-wrap items-center gap-2 text-[0.8rem]"
          style={{ color: "var(--muted-foreground)" }}
          aria-label="Breadcrumb"
        >
          <Link to={paths.home} className="no-underline" style={{ color: "inherit" }}>
            Docs
          </Link>
          <ChevronRight className="size-3" aria-hidden />
          {data.categorySlug ? (
            <Link
              to={paths.category(data.categorySlug)}
              className="no-underline"
              style={{ color: "inherit" }}
            >
              {data.categoryName}
            </Link>
          ) : (
            <span>{data.categoryName}</span>
          )}
          <ChevronRight className="size-3" aria-hidden />
          <span style={{ color: "var(--foreground)" }}>{data.title}</span>
        </nav>

        <h1
          className="mb-2 text-[2rem] font-bold leading-tight tracking-tight"
          style={{ color: "var(--foreground)" }}
        >
          {data.title}
        </h1>
        {data.summary && (
          <p className="mb-8 text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
            {data.summary}
          </p>
        )}

        {/* Sanitized server-side on write (KbHtmlSanitizer), and again here on render
            against the same allowlist — see shared/lib/safe-html.ts. */}
        <div
          className="kb-prose"
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(data.contentHtml) }}
        />

        {data.showLastUpdated && lastUpdated && (
          <p className="mt-10 text-xs" style={{ color: "var(--muted-foreground)" }}>
            Last updated{" "}
            {new Date(lastUpdated).toLocaleDateString(undefined, {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        )}

        {(data.previous || data.next) && (
          <div className="mt-14 flex gap-4 border-t pt-8" style={{ borderColor: "var(--border)" }}>
            {data.previous && (
              <Link
                to={paths.article(data.previous.slug)}
                className="flex-1 rounded-lg border px-4 py-3 no-underline"
                style={{ background: "var(--card)", borderColor: "var(--border)" }}
              >
                <span className="block text-xs" style={{ color: "var(--muted-foreground)" }}>
                  ← Previous
                </span>
                <span
                  className="block text-[0.92rem] font-semibold"
                  style={{ color: "var(--foreground)" }}
                >
                  {data.previous.title}
                </span>
              </Link>
            )}
            {data.next && (
              <Link
                to={paths.article(data.next.slug)}
                className="flex-1 rounded-lg border px-4 py-3 text-right no-underline"
                style={{ background: "var(--card)", borderColor: "var(--border)" }}
              >
                <span className="block text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Next →
                </span>
                <span
                  className="block text-[0.92rem] font-semibold"
                  style={{ color: "var(--foreground)" }}
                >
                  {data.next.title}
                </span>
              </Link>
            )}
          </div>
        )}
      </main>

      {data.toc && data.toc.length > 0 && <TableOfContents entries={data.toc} />}
    </div>
  );
}

/** The "On this page" rail, with the current heading highlighted as you scroll. */
function TableOfContents({ entries }: { entries: TocEntry[] }) {
  const [activeId, setActiveId] = useState<string | null>(entries[0]?.id ?? null);

  useEffect(() => {
    const onScroll = () => {
      // The active heading is the last one whose top has passed the offset —
      // computed from positions rather than IntersectionObserver so that a short
      // final section still activates when the page bottoms out.
      let current = entries[0]?.id ?? null;

      for (const entry of entries) {
        const element = document.getElementById(entry.id);
        if (!element) continue;
        if (element.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
          current = entry.id;
        }
      }

      setActiveId(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [entries]);

  return (
    <aside
      className="sticky hidden shrink-0 overflow-y-auto py-11 pl-2 pr-6 xl:block"
      style={{ top: 60, width: 232, height: "calc(100vh - 60px)" }}
      aria-label="On this page"
    >
      <div className="mb-3 text-[0.78rem] font-semibold" style={{ color: "var(--foreground)" }}>
        On this page
      </div>
      <div className="flex flex-col border-l" style={{ borderColor: "var(--border)" }}>
        {entries.map((entry) => (
          <a
            key={entry.id}
            href={`#${entry.id}`}
            className="py-1 pl-3 text-[0.82rem] no-underline"
            style={{
              color: activeId === entry.id ? "var(--primary)" : "var(--muted-foreground)",
              fontWeight: activeId === entry.id ? 600 : 400,
              paddingLeft: entry.level > 2 ? "1.5rem" : undefined,
            }}
          >
            {entry.text}
          </a>
        ))}
      </div>
    </aside>
  );
}
